import axios from "axios";
import { getFirstAccessibleTenantPath } from "../config/routePermissions";
import {
  clearSession,
  ensureValidSession,
  getAccessToken,
  getRefreshToken,
  logoutSession,
  refreshAccessTokenSingleFlight,
  setSession,
} from "./authSession";
import { decodeJwtPayload } from "../utils/jwt";

// -------------------- Axios Instances --------------------
const api = axios.create({
  baseURL: process.env.REACT_APP_BASE_URL,
});

const mockApi = axios.create({
  baseURL: process.env.REACT_APP_MOCK_API_URL,
  headers: { "Content-Type": "application/json",  },
});

// -------------------- Redirect Flag --------------------
let isRedirecting = false; // prevent duplicate redirects

export const resetRedirectFlag = () => {
  isRedirecting = false;
};

const RBAC_ACCESS_SUMMARY_KEY = "rbac_access_summary";

/** sessionStorage keys — optional scope sent on user API calls (store/branch filters). */
const TENANT_SCOPE_STORE_KEY = "onehub_tenant_store_id";
const TENANT_SCOPE_BRANCH_KEY = "onehub_tenant_branch_id";

export function setTenantApiScope(storeId, branchId) {
  try {
    if (storeId) sessionStorage.setItem(TENANT_SCOPE_STORE_KEY, String(storeId));
    else sessionStorage.removeItem(TENANT_SCOPE_STORE_KEY);
    if (branchId) sessionStorage.setItem(TENANT_SCOPE_BRANCH_KEY, String(branchId));
    else sessionStorage.removeItem(TENANT_SCOPE_BRANCH_KEY);
  } catch {
    /* ignore */
  }
}

export function getTenantApiScope() {
  try {
    return {
      storeId: sessionStorage.getItem(TENANT_SCOPE_STORE_KEY) || null,
      branchId: sessionStorage.getItem(TENANT_SCOPE_BRANCH_KEY) || null,
    };
  } catch {
    return { storeId: null, branchId: null };
  }
}

export function clearTenantApiScope() {
  try {
    sessionStorage.removeItem(TENANT_SCOPE_STORE_KEY);
    sessionStorage.removeItem(TENANT_SCOPE_BRANCH_KEY);
  } catch {
    /* ignore */
  }
}

export function loadCachedAccessSummary() {
  try {
    const raw = localStorage.getItem(RBAC_ACCESS_SUMMARY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** True when signed-in tenant user is staff (RBAC summary or JWT). */
export function isStaffTenantUser() {
  try {
    const summary = loadCachedAccessSummary();
    if (summary?.user?.user_type === "staff") return true;
    const token = getAccessToken() || localStorage.getItem("token");
    if (!token) return false;
    const payload = decodeJwtPayload(token);
    return (
      payload?.user_type === "staff" || payload?.user?.user_type === "staff"
    );
  } catch {
    return false;
  }
}

export function clearRbacAccessSummary() {
  try {
    localStorage.removeItem(RBAC_ACCESS_SUMMARY_KEY);
  } catch {
    /* ignore */
  }
}

/** Load RBAC access summary for the /v1/users/:id/access-summary endpoint. */
export async function fetchAccessSummary(userId) {
  if (!userId) return null;
  const res = await api.get(`/v1/users/${encodeURIComponent(userId)}/access-summary`);
  const data = res.data;
  try {
    localStorage.setItem(RBAC_ACCESS_SUMMARY_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
  return data;
}

async function syncAccessSummaryFromToken(token) {
  try {
    const payload = decodeJwtPayload(token);
    const userId = payload?.user?.id;
    if (userId) await fetchAccessSummary(userId);
  } catch {
    clearRbacAccessSummary();
  }
}

/** Client-side permission check (server uses Casbin). Body: { resource, action, storeId?, branchId? } */
export async function checkAccess(body) {
  const res = await api.post("/v1/auth/check-access", body);
  return res.data;
}

function axiosRequestHasQueryParam(config, name) {
  const fromParams = config.params?.[name];
  if (fromParams != null && fromParams !== "") return true;
  const url = String(config.url || "");
  const q = url.indexOf("?");
  if (q === -1) return false;
  try {
    return new URLSearchParams(url.slice(q + 1)).has(name);
  } catch {
    return false;
  }
}

// -------------------- Attach Tokens --------------------
api.interceptors.request.use(async (config) => {
  if (!config.url?.startsWith("/admin") && !config.url?.includes("/auth/refresh") && !config.url?.includes("/login")) {
    await ensureValidSession().catch(() => {});
  }

  config.headers = config.headers || {};
  config.headers["X-Client-Platform"] = "web";

  const token = getAccessToken() || localStorage.getItem("token");
  const adminToken = localStorage.getItem("adminToken");
  // useAdminAuth: set by callers (e.g. trackHubApi on /admin/*) so non-/admin URLs
  // still authenticate as platform admin when both tokens exist.
  const useAdminAuth =
    config.useAdminAuth === true ||
    (config.url && String(config.url).startsWith("/admin"));

  if (useAdminAuth && adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  } else if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  } else if (adminToken) {
    config.headers.Authorization = `Bearer ${adminToken}`;
  }

  if (token && config.url && !config.url.startsWith("/admin") && !useAdminAuth) {
    // List/catalog endpoints: backend + page-level filters own scoping. Do not inject
    // dashboard session storeId/branchId here — it breaks company/admin (e.g. catalog
    // store ownership check) and hides tenant-wide lists. Staff already relied on this skip.
    const tenantListRoutes = ["/categories", "/products", "/catalog", "/v2.1/catalog", "/trackhub"];
    const shouldSkipAutoParams = tenantListRoutes.some((route) =>
      String(config.url || "").includes(route)
    );

    // List team members tenant-wide when session has a dashboard store scope —
    // do not imply `storeId` (backend merges owned stores when omitted for company/admin).
    const urlPathOnly = String(config.url || "").split("?")[0];
    const methodLower = String(config.method || "get").toLowerCase();
    // GET /apps: tenant-wide integrations list; optional ?storeId= filters. Session storeId
    // would narrow to one store and must not be implied from dashboard scope (same idea as
    // tenantListRoutes). Do not use includes("/apps") — that matches /stores/:id/apps.
    const isAppsRootListGet =
      String(config.method || "get").toLowerCase() === "get" &&
      urlPathOnly === "/apps";
    const isStaffUsersListGet =
      String(config.method || "get").toLowerCase() === "get" &&
      urlPathOnly === "/v1/staff-users";
    const isStaffUsersIdentityGet =
      String(config.method || "get").toLowerCase() === "get" &&
      urlPathOnly === "/v1/staff-users/check-identity";

    // Roles API: staff must scope with restaurantId in callers; injecting session storeId
    // yields GET ...?storeId=... and the backend returns 403 for staff (company/admin OK).
    const isRolesApiGet =
      String(config.method || "get").toLowerCase() === "get" &&
      urlPathOnly.startsWith("/v1/roles");
    // POST account-level roles must not get session storeId on the query string.
    const isRolesApiWrite =
      ["post", "put", "patch"].includes(methodLower) &&
      urlPathOnly.startsWith("/v1/roles");

    // Store CRUD on `/stores/:storeId` — URL identifies the store; injecting dashboard storeId
    // breaks staff scope middleware (query wins over params before BE fix) and mismatches handlers.
    const isStoresRootByIdWrite =
      ["put", "patch", "delete"].includes(methodLower) &&
      /^\/stores\/[^/]+$/.test(urlPathOnly);

    // Order reports and sales reports: empty date/brand/status filters mean all
    // data. Do not inject dashboard session storeId/branchId over unselected filters.
    const isOrderReportsGet =
      methodLower === "get" && urlPathOnly.startsWith("/order-reports");
    const isSalesReportsGet =
      methodLower === "get" && urlPathOnly.startsWith("/sales-reports");

    // Dashboard widgets pass explicit brandId/branchId/categoryId — skip session scope injection.
    const isDashboardApiGet =
      methodLower === "get" && urlPathOnly.startsWith("/dashboard");

    if (
      !shouldSkipAutoParams &&
      !isAppsRootListGet &&
      !isStaffUsersListGet &&
      !isStaffUsersIdentityGet &&
      !isRolesApiGet &&
      !isRolesApiWrite &&
      !isStoresRootByIdWrite &&
      !isOrderReportsGet &&
      !isSalesReportsGet &&
      !isDashboardApiGet
    ) {
      const { storeId, branchId } = getTenantApiScope();
      if (storeId || branchId) {
        const params = { ...(config.params || {}) };
        const hasStore =
          axiosRequestHasQueryParam(config, "storeId") ||
          axiosRequestHasQueryParam(config, "restaurantId") ||
          params.storeId != null ||
          params.restaurantId != null;
        const hasBranch = axiosRequestHasQueryParam(config, "branchId") || params.branchId != null;
        if (storeId && !hasStore) {
          params.storeId = storeId;
        }
        if (branchId && !hasBranch) {
          params.branchId = branchId;
        }
        config.params = params;
      }
    }
  }

  return config;
});

// -------------------- Response Interceptor --------------------
export const setupAxiosInterceptors = (navigate) => {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (
        error.response &&
        error.response.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes("/auth/refresh") &&
        !originalRequest.url?.includes("/login")
      ) {
        const isAdminRequest = originalRequest.url?.startsWith("/admin");
        if (!isAdminRequest && getRefreshToken()) {
          originalRequest._retry = true;
          try {
            const newToken = await refreshAccessTokenSingleFlight();
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return api(originalRequest);
          } catch (refreshError) {
            // fall through to logout redirect
          }
        }
      }

      if (error.response && error.response.status === 401) {
        if (isRedirecting) {
          console.log("⏳ Already redirecting, skip this 401.");
          return Promise.reject(error);
        }

        const currentPath = window.location.pathname;
        const currentQuery = window.location.search;
        const currentPathWithQuery = currentPath + currentQuery;
        
        console.log("🚨 401 detected. Current path:", currentPathWithQuery);

        const publicRoutes = ["/", "/register", "/forgot-password", "/reset-password", "/partner-sign-in"];
        const publicAdminRoutes = ["/admin/login"];

        // skip redirect if already on login
        if (publicAdminRoutes.includes(currentPath)) return Promise.reject(error);
        if (publicRoutes.includes(currentPath)) return Promise.reject(error);

        const isAdminRequest = error.config?.url?.startsWith("/admin");

        if (isAdminRequest) {
          console.log("⏰ Clearing admin session.");
          localStorage.removeItem("adminToken");
          localStorage.removeItem("adminUser");

          isRedirecting = true;
          navigate(`/admin/login?redirect=${encodeURIComponent(currentPathWithQuery)}`, { replace: true });
        } else {
          console.log("⏰ Clearing user session.");
          await logoutSession();
          clearRbacAccessSummary();
          clearTenantApiScope();

          isRedirecting = true;

          // Check if current URL is an OAuth callback
          const isOAuthCallback = currentPath.includes("/oauth/") && currentPath.includes("/callback");
          
          // For OAuth callbacks, preserve the entire URL including all query parameters
          if (isOAuthCallback) {
            const fullCallbackUrl = window.location.pathname + window.location.search + window.location.hash;
            console.log("🔗 Preserving OAuth callback URL:", fullCallbackUrl);
            navigate(`/?redirect=${encodeURIComponent(fullCallbackUrl)}`, { replace: true });
          } else {
            // For regular routes, preserve only the path and query
            navigate(`/?redirect=${encodeURIComponent(currentPathWithQuery)}`, { replace: true });
          }
        }

        return Promise.reject(error);
      }

      return Promise.reject(error);
    }
  );
};

// -------------------- Session Setters --------------------
export const setUserSession = (token, user, sessionExtras = {}) => {
  setSession({
    accessToken: token,
    refreshToken: sessionExtras.refreshToken,
    user,
    accessTokenExpiresAt: sessionExtras.accessTokenExpiresAt,
    refreshTokenExpiresAt: sessionExtras.refreshTokenExpiresAt,
  });
  const userType = sessionExtras.user_type || user?.user_type;
  if (userType) {
    localStorage.setItem("user_type", userType);
  }
  resetRedirectFlag();
  syncAccessSummaryFromToken(token).catch(() => {});
};

export { ensureValidSession, logoutSession, setSession, getAccessToken, getRefreshToken };

export const setAdminSession = (token, adminUser) => {
  localStorage.setItem("adminToken", token);
  localStorage.setItem("adminUser", JSON.stringify(adminUser));
  resetRedirectFlag();
};

// -------------------- Login Redirect Handler --------------------
// Call this function after successful login to redirect to the preserved URL
export const handlePostLoginRedirect = (navigate) => {
  const urlParams = new URLSearchParams(window.location.search);
  const redirectParam = urlParams.get('redirect');
  
  if (redirectParam) {
    const decodedRedirect = decodeURIComponent(redirectParam);
    console.log("🔄 Redirecting to preserved URL:", decodedRedirect);
    
    // Clear the redirect parameter from URL
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('redirect');
    window.history.replaceState({}, document.title, newUrl.toString());
    
    // Navigate to the preserved URL
    navigate(decodedRedirect, { replace: true });
  } else {
    const summary = loadCachedAccessSummary();
    navigate(getFirstAccessibleTenantPath(summary), { replace: true });
  }
};

// -------------------- Export --------------------
export { api, mockApi };